import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import { Container, Card, Row, Col, Form, Button, Badge, Alert } from 'react-bootstrap';
import moment from 'moment';
import AsyncSelect from 'react-select/async';
import {
  appointmentService,
  locationService,
  userService,
  groupService
} from '../../services/api';
import AppointmentModal from './AppointmentModal';
import MobileDayStrip from './MobileDayStrip';
import { formatGroupOptionLabel } from '../../helpers/groupHelpers';
import './PlannerCalendar.css';

const PlannerCalendar = () => {
  const calendarRef = useRef(null);

  // Responsive state (< 768px = mobile)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Filters State
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedCoach, setSelectedCoach] = useState('');
  const [selectedGroupOption, setSelectedGroupOption] = useState(null);

  // Options State
  const [locations, setLocations] = useState([]);
  const [coaches, setCoaches] = useState([]);

  // Appointments State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Selected date for mobile view
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [slotDate, setSlotDate] = useState(null);
  const [slotStartTime, setSlotStartTime] = useState(null);
  const [slotEndTime, setSlotEndTime] = useState(null);

  // Search Debounce Ref
  const groupSearchTimeoutRef = useRef(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (groupSearchTimeoutRef.current) clearTimeout(groupSearchTimeoutRef.current);
    };
  }, []);

  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        if (mobile && calendarApi.view.type !== 'timeGridDay') {
          calendarApi.changeView('timeGridDay');
        }
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [locRes, coachRes] = await Promise.all([
          locationService.getAll(false),
          userService.getUsers({ role: 'tech', limit: 100 })
        ]);
        if (locRes.success) {
          const locList = Array.isArray(locRes.data) ? locRes.data : (locRes.data?.items || []);
          setLocations(locList);
        }
        if (coachRes.success) {
          const coachList = Array.isArray(coachRes.data)
            ? coachRes.data
            : (coachRes.data?.items || coachRes.data?.users || []);
          setCoaches(coachList);
        }
      } catch (err) {
        console.error('Error loading filters in Planner:', err);
      }
    };
    loadFilters();
  }, []);

  // Load groups asynchronously with debounce (matches AppointmentModal)
  const loadGroupsAsync = (inputValue) => {
    return new Promise((resolve) => {
      if (groupSearchTimeoutRef.current) {
        clearTimeout(groupSearchTimeoutRef.current);
      }

      groupSearchTimeoutRef.current = setTimeout(async () => {
        try {
          if (!inputValue || inputValue.length < 2) {
            resolve([]);
            return;
          }

          const response = await groupService.getGroups({
            search: inputValue,
            page: 1,
            limit: 50
          });

          if (response.success) {
            const items = Array.isArray(response.data)
              ? response.data
              : (response.data?.items || response.data?.groups || []);

            const options = items.map((g) => ({
              value: g.id,
              label: formatGroupOptionLabel(g),
              group: g
            }));

            resolve(options);
          } else {
            resolve([]);
          }
        } catch (err) {
          console.error('Error loading groups in Planner:', err);
          resolve([]);
        }
      }, 300);
    });
  };

  // Fetch appointments for calendar
  const fetchAppointments = useCallback(async () => {
    if (!calendarRef.current) return;
    const calendarApi = calendarRef.current.getApi();
    const currentView = calendarApi.view;

    setLoading(true);
    setError('');

    const res = await appointmentService.getAppointments({
      start: currentView.activeStart,
      end: currentView.activeEnd,
      locationId: selectedLocation || undefined,
      coachId: selectedCoach || undefined,
      groupId: selectedGroupOption?.value || undefined
    });

    setLoading(false);

    if (res.success && res.data) {
      const appList = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      const formattedEvents = appList.map((app) => {
        let className = 'event-status-scheduled';
        if (app.status === 'completed') className = 'event-status-completed';
        if (app.status === 'cancelled') className = 'event-status-cancelled';

        return {
          id: app.id,
          title: app.title,
          start: app.startTime,
          end: app.endTime,
          className,
          extendedProps: {
            locationName: app.locationName,
            coachName: app.coachName,
            groupName: app.groupName,
            status: app.status,
            participantCount: app.participantCount,
            hasConflicts: app.hasConflicts
          }
        };
      });
      setEvents(formattedEvents);
    } else {
      const errText = typeof res.message === 'string'
        ? res.message
        : (res.message?.message || 'Errore nel caricamento degli appuntamenti');
      setError(errText);
    }
  }, [selectedLocation, selectedCoach, selectedGroupOption]);

  // Re-fetch on filter change
  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Handle slot selection (desktop click & drag on grid)
  const handleSelectSlot = (selectInfo) => {
    setSelectedAppointmentId(null);
    setSlotDate(selectInfo.start);
    setSlotStartTime(moment(selectInfo.start).format('HH:mm'));
    setSlotEndTime(moment(selectInfo.end).format('HH:mm'));
    setShowModal(true);
  };

  // Handle event click
  const handleEventClick = (clickInfo) => {
    setSelectedAppointmentId(clickInfo.event.id);
    setSlotDate(null);
    setSlotStartTime(null);
    setSlotEndTime(null);
    setShowModal(true);
  };

  // Handle event drag & drop (desktop)
  const handleEventDrop = async (dropInfo) => {
    const { event } = dropInfo;
    const res = await appointmentService.update(event.id, {
      title: event.title,
      startTime: event.start,
      endTime: event.end,
      locationId: event.extendedProps.locationId || undefined,
      coachId: event.extendedProps.coachId || undefined
    });

    if (!res.success) {
      if (res.isConflict) {
        alert('Attenzione: lo spostamento crea un conflitto orario!');
      } else {
        alert(res.message || 'Errore nello spostamento dell\'appuntamento');
      }
      dropInfo.revert();
    } else {
      fetchAppointments();
    }
  };

  // Handle event resize (desktop)
  const handleEventResize = async (resizeInfo) => {
    const { event } = resizeInfo;
    const res = await appointmentService.update(event.id, {
      title: event.title,
      startTime: event.start,
      endTime: event.end
    });

    if (!res.success) {
      alert(res.message || 'Errore nella modifica della durata');
      resizeInfo.revert();
    } else {
      fetchAppointments();
    }
  };

  // Mobile date jump
  const handleMobileSelectDate = (date) => {
    setSelectedDate(date);
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(date);
    }
  };

  // Render custom event content
  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const { locationName, coachName, participantCount, hasConflicts } = event.extendedProps;
    return (
      <div className="fc-event-custom-content p-1">
        <div className="fw-bold text-truncate">{event.title}</div>
        <div className="small text-truncate opacity-75">
          <i className="fas fa-map-marker-alt me-1"></i>
          {locationName} | <i className="fas fa-user-ninja me-1"></i>{coachName}
        </div>
        <div className="d-flex justify-content-between align-items-center mt-1">
          <small className="opacity-75">
            <i className="fas fa-users me-1"></i>
            {participantCount}
          </small>
          {hasConflicts && (
            <span className="event-conflict-badge" title="Conflitto orario approvato da Admin">
              Conflitto
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="planner-container py-3">
      {/* Top Header & Actions */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-0 text-primary fw-bold">
            <i className="fas fa-calendar-alt me-2"></i>
            Planner & Calendario Lezioni
          </h4>
          <small className="text-muted">
            Pianificazione lezioni, appelli presenze, gestione stanze e controllo sovrapposizioni orarie
          </small>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={fetchAppointments}
            disabled={loading}
            title="Aggiorna calendario"
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setSelectedAppointmentId(null);
              setSlotDate(new Date());
              setSlotStartTime('15:00');
              setSlotEndTime('16:00');
              setShowModal(true);
            }}
          >
            <i className="fas fa-plus me-1"></i>
            Nuovo Appuntamento
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {/* Filters Card */}
      <Card className="mb-3 border-0 shadow-sm bg-light">
        <Card.Body className="py-2 px-3">
          <Row className="g-2 align-items-center">
            <Col xs={12} sm={4} md={3}>
              <Form.Select
                size="sm"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="">Tutte le Stanze / Spazi</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={4} md={3}>
              <Form.Select
                size="sm"
                value={selectedCoach}
                onChange={(e) => setSelectedCoach(e.target.value)}
              >
                <option value="">Tutti i Coach</option>
                {coaches.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={4} md={3}>
              <AsyncSelect
                cacheOptions
                defaultOptions={false}
                loadOptions={loadGroupsAsync}
                onChange={(option) => setSelectedGroupOption(option)}
                value={selectedGroupOption}
                placeholder="Cerca gruppo per nome..."
                noOptionsMessage={({ inputValue }) =>
                  !inputValue || inputValue.length < 2
                    ? "Digita almeno 2 caratteri per cercare..."
                    : "Nessun gruppo trovato"
                }
                loadingMessage={() => "Ricerca in corso..."}
                isClearable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '38px',
                    fontSize: '0.875rem'
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999
                  })
                }}
              />
            </Col>
            <Col xs={12} md={3} className="text-md-end text-muted small">
              <span className="me-2">
                <Badge bg="primary" className="me-1">&nbsp;</Badge> In Programma
              </span>
              <span className="me-2">
                <Badge bg="success" className="me-1">&nbsp;</Badge> Completato
              </span>
              <span>
                <Badge bg="secondary" className="me-1">&nbsp;</Badge> Annullato
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Mobile Day Strip (Horizontal scroll on smartphones) */}
      {isMobile && (
        <MobileDayStrip
          selectedDate={selectedDate}
          onSelectDate={handleMobileSelectDate}
        />
      )}

      {/* Calendar Card */}
      <Card className="planner-calendar-card p-3">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
          headerToolbar={
            isMobile
              ? {
                  left: 'prev,next today',
                  center: 'title',
                  right: 'timeGridDay,listDay'
                }
              : {
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }
          }
          locale={itLocale}
          events={events}
          editable={!isMobile} // Disable drag & drop on mobile to prevent scrolling issues
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          slotMinTime="07:00:00"
          slotMaxTime="23:00:00"
          slotDuration="00:30:00"
          allDaySlot={false}
          nowIndicator={true}
          select={handleSelectSlot}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          datesSet={fetchAppointments}
          eventContent={renderEventContent}
          height="auto"
        />
      </Card>

      {/* Floating Action Button on Mobile */}
      {isMobile && (
        <Button
          variant="primary"
          className="planner-fab"
          onClick={() => {
            setSelectedAppointmentId(null);
            setSlotDate(selectedDate);
            setSlotStartTime('15:00');
            setSlotEndTime('16:00');
            setShowModal(true);
          }}
          aria-label="Nuovo Appuntamento"
        >
          <i className="fas fa-plus"></i>
        </Button>
      )}

      {/* Appointment Create/Edit Modal */}
      {showModal && (
        <AppointmentModal
          show={showModal}
          onHide={() => setShowModal(false)}
          appointmentId={selectedAppointmentId}
          initialDate={slotDate}
          initialStartTime={slotStartTime}
          initialEndTime={slotEndTime}
          onSuccess={fetchAppointments}
        />
      )}
    </Container>
  );
};

export default PlannerCalendar;
